import React, { useState, useRef, useEffect } from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import ActionMenu from "./ActionMenu";

/**
 * View component - used to declare views within EntityOperationBar
 * This component doesn't render anything directly - it's used for configuration
 */
function View({ mode, icon, tooltip, children }) {
    return null; // This component is only used for configuration
}

/**
 * EntityOperationBar - A reusable component for entity list operations
 *
 * @param {Object} entity - Entity names { singular: "Zone", plural: "Zones" }
 * @param {boolean} allowSearch - Show/hide search bar (default: true)
 * @param {boolean} allowAdd - Show/hide add button (default: true)
 * @param {boolean} allowDelete - Show/hide delete button (default: true)
 * @param {boolean} allowEdit - Show/hide edit button (default: true)
 * @param {boolean} allowExtendedActions - Show/hide extended actions dropdown (default: false)
 * @param {Array} extendedActions - Array of action objects: [{ label, icon, onClick, disabled }]
 * @param {Array} selectedRows - Currently selected rows
 * @param {boolean} isDeleting - Delete operation in progress
 * @param {number} deleteDone - Number of items deleted (for progress)
 * @param {string} searchPlaceholder - Custom search placeholder
 * @param {string} addButtonText - Custom add button text (default: "Add {singular}")
 * @param {string} deleteButtonText - Custom delete button text (default: "Delete {count} {singular/plural}")
 * @param {string} editButtonText - Custom edit button text (default: "Edit")
 * @param {boolean} deleteDisabled - Disable delete button
 * @param {string} deleteTooltip - Custom tooltip for disabled delete button
 * @param {Function} onSearch - Search handler (text) => void
 * @param {Function} onAdd - Add handler () => void
 * @param {Function} onViewChange - View change handler (mode) => void
 * @param {Function} onDelete - Delete handler () => void
 * @param {Function} onEdit - Edit handler () => void
 * @param {ReactNode} children - EntityOperationBar.View components
 */
function EntityOperationBar({
    entity = { singular: "Item", plural: "Items" },
    allowSearch = true,
    allowAdd = true,
    allowDelete = true,
    allowEdit = true,
    allowExtendedActions = false,
    allowShowInactive = false,
    showInactive = false,
    extendedActions = [],
    selectedRows = [],
    isDeleting = false,
    deleteDone = 0,
    searchPlaceholder,
    addButtonText,
    deleteButtonText,
    editButtonText,
    deleteDisabled = false,
    deleteTooltip,
    defaultView,
    onSearch,
    onAdd,
    onViewChange,
    onDelete,
    onEdit,
    onShowInactiveChange,
    children,
}) {
    // Extract views from children
    const views = React.Children.toArray(children)
        .filter((child) => child.type === View)
        .map((child) => ({
            mode: child.props.mode,
            icon: child.props.icon,
            tooltip: child.props.tooltip,
            component: () => child.props.children,
        }));

    const [searchText, setSearchText] = useState("");
    const [currentView, setCurrentView] = useState(
        defaultView || views[0]?.mode || null
    );
    const [showExtendedMenu, setShowExtendedMenu] = useState(false);
    const extendedMenuRef = useRef(null);
    const extendedButtonRef = useRef(null);

    useEffect(() => {
        if (defaultView) {
            setCurrentView(defaultView);
        }
    }, [defaultView]);

    const isSingle = selectedRows.length === 1;
    const isAnySelected = selectedRows.length > 0;

    // Close menu when clicking outside
    useEffect(() => {
        if (!showExtendedMenu) return;
        const handleClickOutside = (e) => {
            if (
                extendedMenuRef.current &&
                !extendedMenuRef.current.contains(e.target) &&
                extendedButtonRef.current &&
                !extendedButtonRef.current.contains(e.target)
            ) {
                setShowExtendedMenu(false);
            }
        };
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, [showExtendedMenu]);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchText(value);
        if (onSearch) {
            onSearch(value);
        }
    };

    const handleViewChange = (mode) => {
        setCurrentView(mode);
        if (onViewChange) {
            onViewChange(mode);
        }
    };

    const defaultSearchPlaceholder =
        searchPlaceholder || `Search ${entity.plural.toLowerCase()}…`;

    const defaultAddButtonText = addButtonText || `Add ${entity.singular}`;

    const defaultDeleteButtonText =
        deleteButtonText ||
        `Delete ${selectedRows.length} ${
            selectedRows.length === 1 ? entity.singular : entity.plural
        }`;

    const defaultEditButtonText = editButtonText || "Edit";

    // Find the current active view component
    const activeView = views.find((v) => v.mode === currentView);

    return (
        <>
            <div className="d-flex align-items-center justify-content-between mb-3">
                {allowSearch && (
                    <input
                        type="text"
                        className="border rounded p-2"
                        style={{ width: "40%" }}
                        placeholder={defaultSearchPlaceholder}
                        value={searchText}
                        onChange={handleSearchChange}
                    />
                )}

                <div className="d-flex align-items-center">
                    {allowShowInactive && (
                        <div className="d-flex align-items-center me-3">
                            <input
                                type="checkbox"
                                id="show-inactive-cb"
                                className="me-1"
                                checked={showInactive}
                                onChange={(e) => onShowInactiveChange && onShowInactiveChange(e.target.checked)}
                            />
                            <label htmlFor="show-inactive-cb" className="mb-0 text-muted" style={{ fontSize: "0.85rem", cursor: "pointer", userSelect: "none" }}>
                                Show Inactive
                            </label>
                        </div>
                    )}

                    {views.length > 0 && (
                        <div className="mr-4">
                            {views.map((view, index) => (
                                <OverlayTrigger
                                    key={view.mode}
                                    placement="top"
                                    overlay={<Tooltip>{view.tooltip}</Tooltip>}
                                >
                                    <Button
                                        onClick={() =>
                                            handleViewChange(view.mode)
                                        }
                                        title={view.tooltip}
                                        className={`d-inline-flex align-items-center tw-h-10 border border-info ${
                                            index > 0 ? "ms-2" : ""
                                        } ${
                                            currentView === view.mode
                                                ? "bg-info text-white"
                                                : "bg-white text-info"
                                        }`}
                                    >
                                        <i className={`fa ${view.icon}`} />
                                    </Button>
                                </OverlayTrigger>
                            ))}
                        </div>
                    )}

                    {allowExtendedActions &&
                        isAnySelected &&
                        extendedActions.length > 0 && (
                            <div
                                style={{ position: "relative" }}
                                className="me-2"
                            >
                                <Button
                                    ref={extendedButtonRef}
                                    variant="primary"
                                    onClick={() =>
                                        setShowExtendedMenu(!showExtendedMenu)
                                    }
                                    disabled={!isAnySelected}
                                >
                                    Bulk Actions
                                </Button>

                                {showExtendedMenu && (
                                    <ActionMenu
                                        ref={extendedMenuRef}
                                        className="tw-absolute tw-right-0 tw-top-full tw-mt-2 tw-z-[9999]"
                                    >
                                        {extendedActions.map(
                                            (action, index) => (
                                                <ActionMenu.Item
                                                    key={index}
                                                    icon={action.icon}
                                                    label={action.label}
                                                    danger={action.danger}
                                                    disabled={action.disabled}
                                                    withDivider={
                                                        action.withDivider
                                                    }
                                                    onClick={() => {
                                                        if (!action.disabled) {
                                                            action.onClick(
                                                                selectedRows,
                                                            );
                                                            setShowExtendedMenu(
                                                                false,
                                                            );
                                                        }
                                                    }}
                                                />
                                            ),
                                        )}
                                    </ActionMenu>
                                )}
                            </div>
                        )}

                    {allowEdit && isSingle && (
                        <Button
                            className="me-2"
                            variant="outline-primary"
                            disabled={!isSingle || !isAnySelected}
                            onClick={onEdit}
                            title={defaultEditButtonText}
                        >
                            {defaultEditButtonText}
                        </Button>
                    )}

                    {allowDelete && isAnySelected && (
                        <>
                            {deleteDisabled && deleteTooltip ? (
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip>{deleteTooltip}</Tooltip>}
                                >
                                    <span className="d-inline-block">
                                        <Button
                                            className="me-2"
                                            variant="danger"
                                            onClick={onDelete}
                                            disabled={true}
                                            title={deleteTooltip}
                                            aria-live="polite"
                                            style={{ pointerEvents: "none" }}
                                        >
                                            {defaultDeleteButtonText}
                                        </Button>
                                    </span>
                                </OverlayTrigger>
                            ) : (
                                <Button
                                    className="me-2"
                                    variant="danger"
                                    onClick={onDelete}
                                    disabled={
                                        deleteDisabled ||
                                        isDeleting ||
                                        !isAnySelected
                                    }
                                    title={
                                        isDeleting
                                            ? `Deleting ${deleteDone}/${selectedRows.length}`
                                            : defaultDeleteButtonText
                                    }
                                    aria-live="polite"
                                >
                                    {isDeleting ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-2"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                            Deleting {deleteDone}/
                                            {selectedRows.length}
                                        </>
                                    ) : (
                                        defaultDeleteButtonText
                                    )}
                                </Button>
                            )}
                        </>
                    )}

                    {allowAdd && (
                        <Button
                            variant="primary"
                            onClick={onAdd}
                            title={defaultAddButtonText}
                        >
                            {defaultAddButtonText}
                        </Button>
                    )}
                </div>
            </div>

            {/* Render the active view component */}
            {activeView && activeView.component && (
                <>{activeView.component()}</>
            )}
        </>
    );
}

// Attach View as a static property for compound component pattern
EntityOperationBar.View = View;

export default EntityOperationBar;
